import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Store the logo as a data URL for reuse in PDFs
 */
let logoDataUrl = null;

/**
 * Store the application name for PDFs
 */
let appName = 'QuEry-Tool';

// Debug variable to track app name changes
let appNameUpdateCount = 0;

/**
 * Pre-load the logo image to be used in PDFs
 * This should be called once when the application initializes
 * @param {string} logoSrc - The URL of the logo image to preload
 */
export const preloadLogoImage = (logoSrc) => {
  return new Promise((resolve, reject) => {
    console.log('Preloading logo from:', logoSrc);
    
    // Create a new image element
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = function() {
      try {
        console.log('Logo loaded successfully with dimensions:', img.width, 'x', img.height);
        
        // Create a canvas to convert the image to a data URL
        const canvas = document.createElement('canvas');
        
        // Use a reasonable resolution multiplier - reduced from 12 to 4 for better file size
        const resolutionMultiplier = 4;
        
        // Reduce padding around the image
        const padding = 40; // Reduced from 80 to 40
        
        // Calculate dimensions with lower but still good resolution
        const imgWidth = Math.max(img.width || 100, 100) * resolutionMultiplier;
        const imgHeight = Math.max(img.height || 50, 50) * resolutionMultiplier;
        
        // Set canvas dimensions with padding and higher resolution
        canvas.width = imgWidth + padding;
        canvas.height = imgHeight + padding;
        
        console.log(`Canvas dimensions set to: ${canvas.width} x ${canvas.height} (high resolution with ${padding}px padding)`);
        
        // Get context with alpha channel to preserve transparency better
        const ctx = canvas.getContext('2d', { 
          alpha: true, 
          willReadFrequently: true,
          desynchronized: false // Better for image quality
        });
        
        if (!ctx) {
          console.error('Failed to get canvas context');
          reject(new Error('Canvas context not available'));
          return;
        }
        
        // Clear canvas with a white background for better rendering with transparency
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Enable high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw the image with padding (centered) and higher resolution
        const offsetX = padding / 2;
        const offsetY = padding / 2;
        
        // Apply sharper rendering for SVG images
        if (logoSrc.toLowerCase().endsWith('.svg')) {
          // Special handling for vector graphics
          console.log('Using enhanced rendering for SVG image');
          ctx.drawImage(img, offsetX, offsetY, imgWidth, imgHeight);
        } else {
          ctx.drawImage(img, offsetX, offsetY, imgWidth, imgHeight);
        }
        
        // Try multiple image formats in order of preference but with reasonable quality settings
        let formatSuccess = false;
        
        // Try PNG with good quality (0.8 instead of 1.0)
        try {
          logoDataUrl = canvas.toDataURL('image/png', 0.8);
          console.log('Logo converted to high-quality PNG data URL successfully');
          formatSuccess = true;
        } catch (pngError) {
          console.warn('Failed to convert logo to PNG, trying JPEG:', pngError);
        }
        
        // If PNG failed, try JPEG with good quality
        if (!formatSuccess) {
          try {
            logoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
            console.log('Logo converted to high-quality JPEG data URL successfully');
            formatSuccess = true;
          } catch (jpegError) {
            console.warn('Failed to convert logo to JPEG, trying basic format:', jpegError);
          }
        }
        
        // Last resort, try without specifying format
        if (!formatSuccess) {
          try {
            logoDataUrl = canvas.toDataURL();
            console.log('Logo converted to basic data URL');
            formatSuccess = true;
          } catch (basicError) {
            console.error('Failed to convert logo to any format:', basicError);
            logoDataUrl = null;
            reject(new Error('Failed to convert logo to data URL'));
            return;
          }
        }
        
        // Log a preview of the data URL to help with debugging
        if (logoDataUrl) {
          console.log('Logo data URL preview:', logoDataUrl.substring(0, 50) + '...');
          console.log(`Logo data URL length: ${logoDataUrl.length} characters`);
        }
        
        resolve(logoDataUrl);
      } catch (error) {
        console.error('Error processing logo:', error);
        logoDataUrl = null;
        reject(error);
      }
    };
    
    img.onerror = function(e) {
      console.error('Failed to load logo image:', e);
      logoDataUrl = null;
      reject(new Error('Failed to load logo image'));
    };
    
    try {
      // Add cache buster to avoid caching issues
      const cacheBuster = Date.now();
      const url = logoSrc || `${window.location.origin}${process.env.PUBLIC_URL || ''}/logo.svg`;
      
      // Make sure we're using an absolute URL
      if (url.startsWith('/')) {
        img.src = `${window.location.origin}${url}${url.includes('?') ? '&' : '?'}t=${cacheBuster}`;
      } else {
        img.src = url.includes('?') ? `${url}&t=${cacheBuster}` : `${url}?t=${cacheBuster}`;
      }
      
      console.log('Set image src to:', img.src);
    } catch (error) {
      console.error('Error setting image source:', error);
      logoDataUrl = null;
      reject(error);
    }
  });
};

/**
 * Set the application name for PDFs
 * @param {string} name - The application name to use
 */
export const setAppName = (name) => {
  if (name && typeof name === 'string') {
    appNameUpdateCount++;
    console.log(`Setting PDF application name to: "${name}" (update #${appNameUpdateCount})`);
    appName = name;
  } else {
    console.warn('Attempted to set app name with invalid value:', name);
  }
};

/**
 * Generate a PDF report from the evaluation results - optimized for performance
 * @param {Array} results - Array of result objects with name and result properties
 * @param {Object} evaluationDetails - Details about the evaluation that was used
 * @param {boolean} isDemoMode - Flag indicating if demo mode is active
 * @returns {jsPDF} - The PDF document object
 */
export const generateResultPdf = (results, evaluationDetails, isDemoMode = false) => {
  console.log(`Generating PDF with app name: "${appName}" (update count: ${appNameUpdateCount}), Demo Mode: ${isDemoMode}`);
  
  // Create a new PDF document with compression enabled for smaller file size
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    compress: true // Enable compression for smaller file size
  });
  
  try {
    // Add header with logo and app name
    let logoAdded = false;
    
    // Try to add the logo if available, with more optimal settings
    if (logoDataUrl && logoDataUrl.length > 0) {
      try {
        // Get the PDF dimensions in millimeters
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Calculate optimal logo dimensions and position
        const logoWidth = 40; // Reduced from 48 to 40 for smaller file size
        const logoHeight = 20; // Reduced from 24 to 20
        const logoX = pageWidth - logoWidth - 15;
        const logoY = 7;
        
        // Add the image with proper compression
        try {
          doc.addImage({
            imageData: logoDataUrl,
            format: 'PNG',
            x: logoX,
            y: logoY,
            width: logoWidth,
            height: logoHeight,
            alias: 'logo_' + Math.random().toString(36).substring(4), // Shorter random string
            compression: 'MEDIUM'
          });
          logoAdded = true;
        } catch (pngError) {
          try {
            doc.addImage({
              imageData: logoDataUrl,
              format: 'JPEG',
              x: logoX,
              y: logoY,
              width: logoWidth,
              height: logoHeight,
              alias: 'logo_j_' + Math.random().toString(36).substring(4),
              compression: 'MEDIUM'
            });
            logoAdded = true;
          } catch (jpegError) {
            try {
              doc.addImage(logoDataUrl, logoX, logoY, logoWidth, logoHeight);
              logoAdded = true;
            } catch (rawError) {
              console.error('Failed to add logo without format specification:', rawError);
            }
          }
        }
      } catch (logoError) {
        console.error('All attempts to add logo to PDF failed:', logoError);
      }
    }
    
    // If logo couldn't be added, use colored rectangle with text
    if (!logoAdded) {
      doc.setFillColor(41, 128, 185);
      doc.rect(140, 7, 45, 22, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      const logoText = appName.charAt(0).toUpperCase() + (appName.length > 1 ? appName.charAt(1).toLowerCase() : '');
      doc.text(logoText, 155, 20);
    }
    
    // Reset to black for document text
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    
    // Add app name to header
    doc.text(appName, 15, 20);
    
    // Optimize title with text wrapping for long titles
    let title = evaluationDetails.evaluationName || 'Auswertungsergebnisse';
    // Add simple diagnosis info if present - but limit size
    if (evaluationDetails.diagnoses && !title.includes(evaluationDetails.diagnoses)) {
      // Only use the first 20 characters of diagnoses to keep title shorter
      const diagnosisPreview = evaluationDetails.diagnoses.length > 20 
        ? evaluationDetails.diagnoses.substring(0, 20) + '...'
        : evaluationDetails.diagnoses;
      title += ` (${diagnosisPreview})`;
    }
    
    // Split long titles to fit the page width
    doc.setFontSize(14);
    const maxTitleWidth = 180;
    const wrappedTitle = doc.splitTextToSize(title, maxTitleWidth);
    doc.text(wrappedTitle, 15, 30);
    
    // Adjust vertical position based on number of title lines
    let titleEndY = 30 + (wrappedTitle.length * 7);

   
    doc.setTextColor(0, 0, 0);
    
    // Check if results exist
    if (!results || results.length === 0) {
      doc.setFontSize(12);
      doc.text('Keine Ergebnisse vorhanden.', 15, titleEndY + 20);
      return doc;
    }
    
    // Add file information with spacing after confidentiality notice
    doc.setFontSize(11);
    doc.text('Dateiname(n):', 15, titleEndY + 20);
    
    // Optimize file listing
    let y = titleEndY + 26;
    
    // More compact file listing
    for (let i = 0; i < results.length; i++) {
      doc.text(results[i].name, 20, y);
      y += 5; // Reduced from 6 to 5 to save space
    }
    
    // Calculate statistics
    let totalFiles = results.length;
    let totalValidFiles = 0;
    let totalPatients = 0;
    let matchingPatients = 0;
    
    for (const result of results) {
      totalValidFiles++;
      totalPatients += (typeof result.totalPatients === 'number' ? result.totalPatients : 0);
      matchingPatients += (typeof result.matchingPatients === 'number' ? result.matchingPatients : 0);
      
      // Fix counts if needed
      if (result.totalPatients < result.matchingPatients) {
        result.totalPatients = result.matchingPatients;
      }
    }
    
    y += 6; // Reduced from 8 to 6
    doc.text(`Ausgewertete Dateien: ${totalValidFiles} von ${totalFiles}`, 15, y);
    y += 5; // Reduced from 6 to 5
    doc.text(`Patienten insgesamt: ${totalPatients}`, 15, y);
    y += 5; // Reduced from 6 to 5
    doc.text(`Gefundene Patienten (passend zur Auswertung): ${matchingPatients}`, 15, y);
    y += 8; // Reduced from 10 to 8
    
    // Render criteria more efficiently - only if they have content
    const maxWidth = 180;
    
    // Helper function to add criteria text with better spacing
    const addCriteriaText = (text, operator) => {
      const splitText = doc.splitTextToSize(text, maxWidth);
      doc.text(splitText, 15, y);
      y += 5 * splitText.length; // Reduced from 6 to 5 multiplier for more compact layout
    };
    
    // Only add criteria that actually have content
    if (evaluationDetails.baseServices) {
      const opText = evaluationDetails.baseServicesOperator === 0 ? 'UND' : 'ODER';
      addCriteriaText(`Mit Basis-Gebührenordnungsposition(en): ${evaluationDetails.baseServices} (${opText})`, opText);
    }
    
    if (evaluationDetails.services) {
      const opText = evaluationDetails.servicesOperator === 0 ? 'UND' : 'ODER';
      addCriteriaText(`Mit Gebührenordnungsposition(en): ${evaluationDetails.services} (${opText})`, opText);
    }
    
    if (evaluationDetails.notServices) {
      const opText = evaluationDetails.notServicesOperator === 2 ? 'Nicht-UND' : 'Nicht-ODER';
      addCriteriaText(`Ohne Gebührenordnungsposition(en): ${evaluationDetails.notServices} (${opText})`, opText);
    }
    
    if (evaluationDetails.diagnoses) {
      const opText = evaluationDetails.diagnosesOperator === 0 ? 'UND' : 'ODER';
      addCriteriaText(`Mit Diagnose(n): ${evaluationDetails.diagnoses} (${opText})`, opText);
    }
    
    if (evaluationDetails.notDiagnoses) {
      const opText = evaluationDetails.notDiagnosesOperator === 2 ? 'Nicht-UND' : 'Nicht-ODER';
      addCriteriaText(`Ohne Diagnose(n): ${evaluationDetails.notDiagnoses} (${opText})`, opText);
    }
    
    // Date formatting
    const date = new Date();
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    
    doc.text(`Datum: ${formattedDate}`, 15, y);
    y += 10; // Reduced from 12 to 10
    
    // Results section
    doc.setFontSize(14);
    doc.text('Übersicht:', 15, y);
    y += 7; // Reduced from 8 to 7
    
    // File summary table - use standard font size instead of reduced size
    doc.autoTable({
      startY: y,
      head: [['Dateiname', 'Gesamtpatienten', 'Passende Patienten']],
      body: results.map(r => [
        r.name,
        typeof r.totalPatients === 'number' ? r.totalPatients.toString() : 
          (r.matchingPatients > 0 ? r.matchingPatients.toString() : '0'),
        typeof r.matchingPatients === 'number' ? r.matchingPatients.toString() : '0'
      ]),
      theme: 'plain',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 11, // Restored from 9 to standard size 11
        cellPadding: 3 // Restored from 2 to standard padding 3
      }
    });
    
    // Get the final Y position after the table
    y = doc.autoTable.previous.finalY + 12;

    // Add Vorhaltepauschale section for each file if available
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result.vorhaltepauschale && result.vorhaltepauschale.vorhaltepauschale) {
        const vhp = result.vorhaltepauschale.vorhaltepauschale;

        // Check if we need a new page
        if (y > 240) {
          doc.addPage();
          y = 15;
        }

        doc.setFontSize(14);
        doc.text(`Vorhaltepauschale${results.length > 1 ? ` (${result.name})` : ''}:`, 15, y);
        y += 7;

        doc.setFontSize(11);
        if (vhp.quarter) {
          doc.text(`Quartal: Q${vhp.quarter}`, 15, y);
          y += 8;
        }

        // Create table data for Vorhaltepauschale criteria
        if (vhp.criteria && vhp.criteria.length > 0) {
          const vhpTableData = vhp.criteria.map(c => [
            c.kriteriumName,
            `${c.matchingServices} / ${c.totalCases}`,
            `${c.percentage.toFixed(2)}%`,
            `${c.threshold.toFixed(2)}%`,
            c.fulfilled ? 'Ja' : 'Nein'
          ]);

          doc.autoTable({
            startY: y,
            head: [['Kriterium', 'Services/Fälle', 'Erreicht', 'Schwelle', 'Erfüllt']],
            body: vhpTableData,
            theme: 'plain',
            headStyles: { fillColor: [76, 175, 80] },
            margin: { left: 15, right: 15 },
            styles: {
              fontSize: 10,
              cellPadding: 2
            },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 30, halign: 'center' },
              2: { cellWidth: 25, halign: 'right' },
              3: { cellWidth: 25, halign: 'right' },
              4: { cellWidth: 20, halign: 'center' }
            }
          });

          y = doc.autoTable.previous.finalY + 5;

          // Add summary
          if (vhp.summary) {
            doc.setFontSize(10);
            doc.setTextColor(76, 175, 80);
            doc.text(
              `Zusammenfassung: ${vhp.summary.fulfilledCriteria} von ${vhp.summary.totalCriteria} Kriterien erfüllt (${vhp.summary.fulfillmentRate}%)`,
              15,
              y
            );
            doc.setTextColor(0, 0, 0);
            y += 12;
          }
        }
      }
    }

    // Optimize patient details section
    const allPatients = [];
    let hasPatientDetails = false;
    
    // Collect all patients from result files - without patient count limits
    for (const result of results) {
      if (result.patients && result.patients.length > 0) {
        // Add source file information to each patient (no limit)
        result.patients.forEach(patient => {
          allPatients.push({
            ...patient,
            sourceFile: result.name
          });
        });
        hasPatientDetails = true;
      }
    }
    
    // Sort patients and prepare patient details section
    if (hasPatientDetails) {
      // Show all patients without limit, but use a page break if there are many patients
      if (allPatients.length > 100) {
        doc.addPage();
        y = 20;
      }
      
      // Sort patients by last name, then by first name
      allPatients.sort((a, b) => {
        const lastNameComparison = (a.lastname || '').localeCompare(b.lastname || '');
        if (lastNameComparison !== 0) return lastNameComparison;
        return (a.firstname || '').localeCompare(b.firstname || '');
      });
      
      doc.setFontSize(14);
      doc.text('Patientendetails (konsolidiert):', 15, y);
      y += 7;
      
      // Remove duplicates from the combined patient list
      const uniquePatients = [];
      const patientSet = new Set();
      
      for (const patient of allPatients) {
        // Format the birth date for consistent comparison
        let birthdate = patient.birthdate || "";
        if (birthdate && birthdate.match(/^\d{8}$/)) {
          const year = birthdate.substring(0, 4);
          const month = birthdate.substring(4, 6);
          const day = birthdate.substring(6, 8);
          birthdate = `${day}.${month}.${year}`;
        }
        else if (birthdate && !birthdate.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          try {
            const dateParts = new Date(birthdate);
            if (!isNaN(dateParts.getTime())) {
              const day = dateParts.getDate().toString().padStart(2, '0');
              const month = (dateParts.getMonth() + 1).toString().padStart(2, '0');
              const year = dateParts.getFullYear();
              birthdate = `${day}.${month}.${year}`;
            }
          } catch (e) {
            // Keep original if parsing fails 
          }
        }
        
        // Create a unique identifier for each patient
        const patientKey = `${patient.lastname}|${patient.firstname}|${birthdate}|${patient.age}`;
        if (!patientSet.has(patientKey)) {
          patientSet.add(patientKey);
          
          // For the first occurrence of a patient, collect all source files where they appear
          const sourceFiles = allPatients
            .filter(p => 
              p.lastname === patient.lastname && 
              p.firstname === patient.firstname && 
              ((p.birthdate || "") === (patient.birthdate || "")) && 
              p.age === patient.age
            )
            .map(p => p.sourceFile);
            
          // Remove duplicates from source files list
          const uniqueSourceFiles = [...new Set(sourceFiles)];
          
          // Add patient with all source files to unique patients list
          uniquePatients.push({
            ...patient,
            formattedBirthdate: birthdate,
            sourceFiles: uniqueSourceFiles
          });
        }
      }
      
      // Display all patients, but optimize table for better performance with large datasets
      const tableBody = uniquePatients.map((patient, index) => {
        // Format source files as comma-separated list
        // For large lists, show the first 5 files and a count of the rest
        const sourceFilesText = patient.sourceFiles.length > 5 
          ? patient.sourceFiles.slice(0, 5).join(', ') + ` (+ ${patient.sourceFiles.length - 5} weitere)`
          : patient.sourceFiles.join(', ');
        
        // In demo mode, replace actual names with generic placeholders
        const patientName = isDemoMode 
          ? `Nachname${index + 1}, Vorname${index + 1}` 
          : `${patient.lastname}, ${patient.firstname}`;
        
        return [
          patientName,
          patient.age.toString(),
          patient.formattedBirthdate,
          sourceFilesText
        ];
      });
      
      // Add the patients table with standard font size
      doc.autoTable({
        startY: y,
        head: [['Name', 'Alter', 'Geburtsdatum', 'Quelldatei(en)']],
        body: tableBody,
        theme: 'plain',
        headStyles: { fillColor: [100, 149, 237] },
        margin: { left: 15, right: 15 },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 20 }, // Increased back from 15 to 20
          2: { cellWidth: 30 }, // Increased back from 25 to 30
          3: { cellWidth: 'auto' }
        },
        styles: {
          fontSize: 11, // Restored from 9 to standard size 11
          cellPadding: 3, // Restored from 2 to standard padding 3
          overflow: 'linebreak'
        },
        // Add didDrawPage to optimize performance with large tables
        didDrawPage: function(data) {
          // Add page number if the table spans multiple pages and has many rows
          if (uniquePatients.length > 50) {
            doc.setFontSize(9); // Keep small font just for page numbers
            doc.text(
              `Seite ${doc.internal.getNumberOfPages()}`, 
              data.settings.margin.left, 
              doc.internal.pageSize.height - 10
            );
          }
        }
      });
    }
  } catch (error) {
    console.error('Error generating PDF content:', error);
    doc.setFontSize(16);
    doc.text('Fehler beim Erstellen des PDF-Dokuments', 15, 20);
    doc.setFontSize(12);
    doc.text(`Fehlerdetails: ${error.message || 'Unbekannter Fehler'}`, 15, 30);
  }
  
  // Add metadata but keep it minimal
  const pdfTitle = evaluationDetails.evaluationName || `${appName} Auswertung`;
  doc.setProperties({
    title: pdfTitle,
    creator: appName, 
    author: appName
  });
  
  return doc;
};

/**
 * Generate a PDF report from multiple evaluation results
 * @param {Array} batchResults - Array of evaluation results 
 * @param {string} appNameParam - The app name to use in the PDF
 * @param {boolean} isDemoMode - Flag indicating if demo mode is active
 * @returns {jsPDF} - The PDF document object
 */
export const generateBatchResultPdf = (batchResults, appNameParam, isDemoMode = false) => {
  console.log(`Generating batch PDF with app name: "${appNameParam || appName}", Demo Mode: ${isDemoMode}`);
  
  // Create a new PDF document with compression enabled
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    compress: true // Enable compression for smaller file size
  });
  
  try {
    // Add header with logo and app name
    let logoAdded = false;
    
    // Try to add the logo if available
    if (logoDataUrl && logoDataUrl.length > 0) {
      try {
        console.log('Attempting to add logo to batch PDF...');
        
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoWidth = 48; 
        const logoHeight = 24; 
        const logoX = pageWidth - logoWidth - 15;
        const logoY = 7;   
        
        try {
          doc.addImage({
            imageData: logoDataUrl,
            format: 'PNG',
            x: logoX,
            y: logoY,
            width: logoWidth,
            height: logoHeight,
            alias: 'logo_' + Math.random().toString(36).substring(7),
            compression: 'MEDIUM' // Use medium compression instead of NONE
          });
          logoAdded = true;
        } catch (pngError) {
          console.warn('Failed to add logo as PNG to batch PDF, trying as JPEG:', pngError);
          
          try {
            doc.addImage({
              imageData: logoDataUrl,
              format: 'JPEG',
              x: logoX,
              y: logoY,
              width: logoWidth,
              height: logoHeight,
              alias: 'logo_jpeg_' + Math.random().toString(36).substring(7),
              compression: 'MEDIUM' // Use medium compression instead of NONE
            });
            logoAdded = true;
          } catch (jpegError) {
            console.warn('Failed to add logo as JPEG, trying without format:', jpegError);
            
            try {
              doc.addImage(logoDataUrl, logoX, logoY, logoWidth, logoHeight);
              logoAdded = true;
            } catch (rawError) {
              console.error('Failed to add logo without format:', rawError);
            }
          }
        }
      } catch (logoError) {
        console.error('All attempts to add logo to batch PDF failed:', logoError);
      }
    }
    
    // If logo couldn't be added, use colored rectangle with text
    if (!logoAdded) {
      doc.setFillColor(41, 128, 185);
      doc.rect(140, 7, 45, 22, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      const logoText = (appNameParam || appName).charAt(0).toUpperCase() + 
                      ((appNameParam || appName).length > 1 ? (appNameParam || appName).charAt(1).toLowerCase() : '');
      doc.text(logoText, 155, 20);
    }
    
    // Reset to black for document text
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    
    // Add app name to header
    doc.text(appNameParam || appName, 15, 20);
    
    // Title
    doc.text('Mehrfachauswertung', 15, 30);
    
    doc.setTextColor(0, 0, 0);
    
    // Date
    const date = new Date();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const formattedDate = `${day}.${month}.${year}`;
    
    doc.setFontSize(11);
    doc.text(`Datum: ${formattedDate}`, 15, 46);
    
    // Files summary - with calculated height
    let fileY = 52;  // Starting Y position for the file list
    let totalFileListHeight = 0;
    
    if (batchResults.length > 0 && batchResults[0].results.length > 0) {
      const fileNames = [...new Set(batchResults[0].results.map(r => r.name))];
      doc.text('Ausgewertete Dateien:', 15, fileY);
      fileY += 5; // Reduced from 6 to 5
      totalFileListHeight = 5;  // Account for "Ausgewertete Dateien:" header
      
      fileNames.forEach(name => {
        doc.text(name, 20, fileY);
        fileY += 5; // Reduced from 6 to 5
        totalFileListHeight += 5;
      });
      
      // Add separation after the file list - reduced spacing
      fileY += 6; // Reduced from 10 to 6
      totalFileListHeight += 6;
      
      // Add a visual separator line between file list and evaluations
      doc.setDrawColor(200, 200, 200);
      doc.line(15, fileY - 3, 195, fileY - 3); // Moved up by 2mm
      
      // Add spacing after separator - reduced
      fileY += 3; // Reduced from 5 to 3
      totalFileListHeight += 3;
    }
    
    // Calculate dynamic starting position based on how many files were listed
    // Ensure a minimum Y position even with no files - reduced from 70 to 65
    let y = Math.max(65, 52 + totalFileListHeight + 3); // Reduced padding from 5 to 3
    
    // Process each evaluation
    for (let i = 0; i < batchResults.length; i++) {
      const evaluation = batchResults[i];
      
      // Check if we need to add a new page - keep the 220 limit for consistency
      if (y > 220) {
        doc.addPage();
        y = 20;
      }
      
      // Evaluation title
      doc.setFontSize(14);
      // Use splitTextToSize to handle long titles with wrapping
      const titleText = `Auswertung ${i+1}: ${evaluation.evaluationName}`;
      const maxTitleWidth = 180; // Maximum width for title before wrapping
      const wrappedTitle = doc.splitTextToSize(titleText, maxTitleWidth);
      
      // Add the wrapped title
      doc.text(wrappedTitle, 15, y);
      
      // Adjust the y position based on the number of lines in the wrapped title
      // More compact spacing for titles
      y += 6 * wrappedTitle.length + 3; // Reduced from 7 per line and +5 to 6 per line and +3
      
      // Check if we need a page break after the title
      if (y > 220) {
        doc.addPage();
        y = 20;
      }
      
      // Add criteria if available
      const evaluationDetails = evaluation.evaluationDetails || {};
      doc.setFontSize(11);
      
      // Add criteria if available - with more compact spacing
      const maxWidth = 180;
      if (evaluationDetails.baseServices) {
        const opText = evaluationDetails.baseServicesOperator === 0 ? 'UND' : 'ODER';
        const text = `Mit Basis-GOP: ${evaluationDetails.baseServices} (${opText})`;
        const splitText = doc.splitTextToSize(text, maxWidth);
        doc.text(splitText, 15, y);
        y += 5 * splitText.length; // Reduced from 6 * splitText.length + 1 to 5 * splitText.length
        
        // Check if we need a page break
        if (y > 220) {
          doc.addPage();
          y = 20;
        }
      }
      
      if (evaluationDetails.services) {
        const opText = evaluationDetails.servicesOperator === 0 ? 'UND' : 'ODER';
        const text = `Mit GOP: ${evaluationDetails.services} (${opText})`;
        const splitText = doc.splitTextToSize(text, maxWidth);
        doc.text(splitText, 15, y);
        y += 5 * splitText.length; // Reduced from 6 * splitText.length + 1 to 5 * splitText.length
        
        // Check if we need a page break
        if (y > 220) {
          doc.addPage();
          y = 20;
        }
      }
      
      if (evaluationDetails.diagnoses) {
        const opText = evaluationDetails.diagnosesOperator === 0 ? 'UND' : 'ODER';
        const text = `Mit Diagnose: ${evaluationDetails.diagnoses} (${opText})`;
        const splitText = doc.splitTextToSize(text, maxWidth);
        doc.text(splitText, 15, y);
        y += 5 * splitText.length; // Reduced from 6 * splitText.length + 1 to 5 * splitText.length
        
        // Check if we need a page break
        if (y > 220) {
          doc.addPage();
          y = 20;
        }
      }
      
      // Results summary
      const results = evaluation.results || [];
      if (results.length === 0) {
        doc.text('Keine Ergebnisse für diese Auswertung.', 15, y);
        y += 10 // Reduced from 15 to 10
        
        // Add separator between evaluations with more compact spacing
        if (i < batchResults.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.line(15, y - 3, 195, y - 3); // Moved from y-5 to y-3
          y += 8; // Reduced from 15 to 8
          
          // Add page break if near bottom of page
          if (y > 220) {
            doc.addPage();
            y = 20;
          }
        }
        
        continue; // Skip the rest of this iteration
      }
      
      // Calculate totals
      const totalMatchingPatients = results.reduce((sum, r) => sum + (r.matchingPatients || 0), 0);
      const totalPatients = results.reduce((sum, r) => sum + (r.totalPatients || 0), 0);
      
      // Add a summary line in the PDF
      doc.text(`Zusammenfassung: ${totalMatchingPatients} von ${totalPatients} gefundenen Patienten`, 15, y);
      y += 6; // Reduced from 8 to 6
      
      // Check for page break before adding table
      if (y > 200) { // Keep the conservative limit for tables
        doc.addPage();
        y = 20;
      }
      
      // Results table
      doc.autoTable({
        startY: y,
        head: [['Dateiname', 'Gesamtpatienten', 'Passende Patienten']],
        body: results.map(r => [
          r.name,
          typeof r.totalPatients === 'number' ? r.totalPatients.toString() : 
            (r.matchingPatients > 0 ? r.matchingPatients.toString() : '0'),
          typeof r.matchingPatients === 'number' ? r.matchingPatients.toString() : '0'
        ]),
        theme: 'plain',
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 15, right: 15 },
        styles: {
          fontSize: 11, // Restored from reduced size to standard size 11
          cellPadding: 3 // Restored from 2 to standard padding 3
        }
      });
      
      // Get the final Y position after the table and add spacing
      y = doc.autoTable.previous.finalY + 10; // Reduced from 15 to 10
      
      // Ensure we're not too close to the bottom after the table
      if (y > 220) {
        doc.addPage();
        y = 20;
      }
      
      // Collect all patients from all result files
      const allPatients = [];
      let hasPatientDetails = false;
      
      for (const result of results) {
        if (result.patients && result.patients.length > 0) {
          // Add source file information to each patient
          result.patients.forEach(patient => {
            allPatients.push({
              ...patient,
              sourceFile: result.name // Add source file name to each patient
            });
          });
          hasPatientDetails = true;
        }
      }
      
      // If we have patient details, add a patients table
      if (hasPatientDetails) {
        // Add a page break if we're already more than halfway down the page
        // This ensures enough space for the header and at least a few rows
        if (y > 160) { // Increased from 150 to 160 to allow more content on page
          doc.addPage();
          y = 20;
        }
        
        doc.setFontSize(12);
        doc.text('Patientendetails:', 15, y);
        y += 6; // Reduced from 8 to 6
        
        // Sort patients by last name, then by first name
        allPatients.sort((a, b) => {
          const lastNameComparison = (a.lastname || '').localeCompare(b.lastname || '');
          if (lastNameComparison !== 0) return lastNameComparison;
          return (a.firstname || '').localeCompare(b.firstname || '');
        });
        
        // Remove duplicates from the combined patient list
        const uniquePatients = [];
        const patientSet = new Set();
        allPatients.forEach(patient => {
          // Format the birth date to ensure consistent comparison
          let birthdate = patient.birthdate || "";
          // Special handling for YYYYMMDD format (common in medical systems)
          if (birthdate && birthdate.match(/^\d{8}$/)) {
            const year = birthdate.substring(0, 4);
            const month = birthdate.substring(4, 6);
            const day = birthdate.substring(6, 8);
            birthdate = `${day}.${month}.${year}`;
          }
          // If it's not already in the format dd.mm.yyyy, try to format it
          else if (birthdate && !birthdate.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            try {
              const dateParts = new Date(birthdate);
              if (!isNaN(dateParts.getTime())) {
                const day = dateParts.getDate().toString().padStart(2, '0');
                const month = (dateParts.getMonth() + 1).toString().padStart(2, '0');
                const year = dateParts.getFullYear();
                birthdate = `${day}.${month}.${year}`;
              }
            } catch (e) {
              // Keep original if parsing fails 
            }
          }
          
          // Create a unique identifier for each patient
          const patientKey = `${patient.lastname}|${patient.firstname}|${birthdate}|${patient.age}`;
          if (!patientSet.has(patientKey)) {
            patientSet.add(patientKey);
            // For the first occurrence of a patient, collect all source files where they appear
            const sourceFiles = allPatients
              .filter(p => 
                p.lastname === patient.lastname && 
                p.firstname === patient.firstname && 
                ((p.birthdate || "") === (patient.birthdate || "")) && 
                p.age === patient.age
              )
              .map(p => p.sourceFile);
            // Remove duplicates from source files list
            const uniqueSourceFiles = [...new Set(sourceFiles)];
            // Add patient with all source files to unique patients list
            uniquePatients.push({
              ...patient,
              formattedBirthdate: birthdate,
              sourceFiles: uniqueSourceFiles
            });
          }
        });
        
        // Create table rows with formatted data - anonymize in demo mode
        const tableBody = uniquePatients.map((patient, index) => {
          // Format source files as comma-separated list
          const sourceFilesText = patient.sourceFiles.length > 1 
            ? patient.sourceFiles.join(', ')
            : patient.sourceFiles[0];
          
          // In demo mode, replace actual names with generic placeholders
          const patientName = isDemoMode 
            ? `Nachname${index + 1}, Vorname${index + 1}` 
            : `${patient.lastname}, ${patient.firstname}`;
          
          return [
            patientName,
            patient.age.toString(),
            patient.formattedBirthdate,
            sourceFilesText
          ];
        });
        
        // Add the patients table
        doc.autoTable({
          startY: y,
          head: [['Name', 'Alter', 'Geburtsdatum', 'Quelldatei(en)']],
          body: tableBody,
          theme: 'plain',
          headStyles: { fillColor: [100, 149, 237] },
          margin: { left: 15, right: 15 },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 20 },
            2: { cellWidth: 30 },
            3: { cellWidth: 'auto' }
          },
          styles: {
            fontSize: 11,
            cellPadding: 3,
            overflow: 'linebreak'
          },
          // Add page numbering for large tables
          didDrawPage: function(data) {
            // Add page number for multi-page tables
            doc.setFontSize(9);
            doc.text(
              `Seite ${doc.internal.getNumberOfPages()} - ${evaluation.evaluationName}`, 
              data.settings.margin.left, 
              doc.internal.pageSize.height - 10
            );
          }
        });
        
        // Get the final Y position after the table and add extra spacing
        y = doc.autoTable.previous.finalY + 20; // Increased from 12 to 20
      }
      
      // Add separator between evaluations with more space and ALWAYS add a page break
      if (i < batchResults.length - 1) {
        // Always add a new page for the next evaluation for cleaner separation
        doc.addPage();
        y = 20;
      }
    }
  } catch (error) {
    console.error('Error generating batch PDF:', error);
    // Create a simple error PDF
    doc.setFontSize(16);
    doc.text('Fehler beim Erstellen des PDF-Dokuments', 15, 20);
    doc.setFontSize(12);
    doc.text(`Fehlerdetails: ${error.message || 'Unbekannter Fehler'}`, 15, 30);
  }
  
  // Add metadata
  doc.setProperties({
    title: 'Mehrfachauswertung',
    creator: appNameParam || appName,
    author: appNameParam || appName
  });

  return doc;
};

/**
 * Generate a standalone Vorhaltepauschale PDF with statistics only (no patients)
 * @param {Array} results - Array of result objects with Vorhaltepauschale data
 * @param {boolean} isDemoMode - Whether to apply demo mode anonymization
 * @returns {jsPDF} The generated PDF document
 */
export const generateVorhaltepauschaleOnlyPdf = (results, isDemoMode = false) => {
  console.log(`Generating Vorhaltepauschale-only PDF with app name: "${appName}"`);

  // Create a new PDF document with compression enabled
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    compress: true
  });

  try {
    // Add header with logo and app name
    let logoAdded = false;

    // Try to add the logo if available
    if (logoDataUrl && logoDataUrl.length > 0) {
      try {
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoWidth = 40;
        const logoHeight = 20;
        const logoX = pageWidth - logoWidth - 15;
        const logoY = 7;

        try {
          doc.addImage({
            imageData: logoDataUrl,
            format: 'PNG',
            x: logoX,
            y: logoY,
            width: logoWidth,
            height: logoHeight,
            alias: 'logo_' + Math.random().toString(36).substring(4),
            compression: 'MEDIUM'
          });
          logoAdded = true;
        } catch (pngError) {
          try {
            doc.addImage({
              imageData: logoDataUrl,
              format: 'JPEG',
              x: logoX,
              y: logoY,
              width: logoWidth,
              height: logoHeight,
              alias: 'logo_j_' + Math.random().toString(36).substring(4),
              compression: 'MEDIUM'
            });
            logoAdded = true;
          } catch (jpegError) {
            try {
              doc.addImage(logoDataUrl, logoX, logoY, logoWidth, logoHeight);
              logoAdded = true;
            } catch (rawError) {
              console.error('Failed to add logo:', rawError);
            }
          }
        }
      } catch (logoError) {
        console.error('All attempts to add logo failed:', logoError);
      }
    }

    // If logo couldn't be added, use colored rectangle with text
    if (!logoAdded) {
      doc.setFillColor(41, 128, 185);
      doc.rect(140, 7, 45, 22, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      const logoText = appName.charAt(0).toUpperCase() + (appName.length > 1 ? appName.charAt(1).toLowerCase() : '');
      doc.text(logoText, 155, 20);
    }

    // Reset to black for document text
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);

    // Add app name to header
    doc.text(appName, 15, 20);

    // Add title
    doc.setFontSize(14);
    doc.text('Vorhaltepauschale-Auswertung', 15, 30);

    // Add generation timestamp
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const now = new Date();
    const timestamp = `Generiert am ${now.toLocaleDateString('de-DE')} um ${now.toLocaleTimeString('de-DE')}`;
    doc.text(timestamp, 15, 38);
    doc.setTextColor(0, 0, 0);

    let y = 50;

    // Add Vorhaltepauschale sections for each file
    for (let i = 0; i < results.length; i++) {
      const result = results[i];

      // Check if we need a new page
      if (y > 240 && i > 0) {
        doc.addPage();
        y = 15;
      }

      if (result.vorhaltepauschale && result.vorhaltepauschale.vorhaltepauschale) {
        const vhp = result.vorhaltepauschale.vorhaltepauschale;

        // File name header (only if multiple files)
        if (results.length > 1) {
          doc.setFontSize(13);
          doc.setTextColor(41, 128, 185);
          doc.text(`Datei: ${result.name}`, 15, y);
          doc.setTextColor(0, 0, 0);
          y += 7;
        }

        // Quarter information
        doc.setFontSize(11);
        if (vhp.quarter) {
          doc.text(`Quartal: Q${vhp.quarter}`, 15, y);
          y += 8;
        }

        // Create table data for Vorhaltepauschale criteria
        if (vhp.criteria && vhp.criteria.length > 0) {
          const vhpTableData = vhp.criteria.map(c => [
            c.kriteriumName,
            `${c.matchingServices} / ${c.totalCases}`,
            `${c.percentage.toFixed(2)}%`,
            `${c.threshold.toFixed(2)}%`,
            c.fulfilled ? 'Ja' : 'Nein'
          ]);

          doc.autoTable({
            startY: y,
            head: [['Kriterium', 'Services/Fälle', 'Erreicht', 'Schwelle', 'Erfüllt']],
            body: vhpTableData,
            theme: 'plain',
            headStyles: { fillColor: [76, 175, 80] },
            margin: { left: 15, right: 15 },
            styles: {
              fontSize: 10,
              cellPadding: 3
            },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 30, halign: 'center' },
              2: { cellWidth: 25, halign: 'right' },
              3: { cellWidth: 25, halign: 'right' },
              4: { cellWidth: 20, halign: 'center' }
            }
          });

          y = doc.autoTable.previous.finalY + 5;

          // Add summary
          if (vhp.summary) {
            doc.setFontSize(11);
            doc.setTextColor(76, 175, 80);
            doc.text(
              `Zusammenfassung: ${vhp.summary.fulfilledCriteria} von ${vhp.summary.totalCriteria} Kriterien erfüllt (${vhp.summary.fulfillmentRate}%)`,
              15,
              y
            );
            doc.setTextColor(0, 0, 0);
            y += 15;
          }
        } else {
          doc.setFontSize(10);
          doc.setTextColor(200, 0, 0);
          doc.text('Keine Vorhaltepauschale-Kriterien gefunden.', 15, y);
          doc.setTextColor(0, 0, 0);
          y += 10;
        }
      } else {
        // No Vorhaltepauschale data for this file
        if (results.length > 1) {
          doc.setFontSize(13);
          doc.setTextColor(41, 128, 185);
          doc.text(`Datei: ${result.name}`, 15, y);
          doc.setTextColor(0, 0, 0);
          y += 7;
        }

        doc.setFontSize(10);
        doc.setTextColor(200, 0, 0);
        doc.text('Keine Vorhaltepauschale-Daten verfügbar.', 15, y);
        doc.setTextColor(0, 0, 0);
        y += 15;
      }
    }

    // Add footer note
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.text(
      'Hinweis: Diese Auswertung enthält nur Vorhaltepauschale-Statistiken ohne Patientendetails.',
      15,
      pageHeight - 10
    );

  } catch (error) {
    console.error('Error generating Vorhaltepauschale PDF:', error);
    doc.text('Fehler beim Generieren der Vorhaltepauschale-Auswertung', 15, 50);
  }

  return doc;
};

/**
 * Open the PDF in a new browser tab/window with a specific filename
 * @param {jsPDF} doc - The PDF document to open
 * @param {string} filename - Optional filename for the PDF
 */
export const openPdfInNewTab = (doc, filename = null) => {
  try {
    // Create a blob with proper filename
    const pdfBlob = doc.output('blob');
    
    // Create a blob URL
    const blobUrl = URL.createObjectURL(pdfBlob);
    
    if (filename) {
      // If we have a filename, use download attribute with data URL
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      downloadLink.target = '_blank';
      
      // This part is important to use both the download attribute
      // and still open in a new tab
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } else {
      // Fallback to old behavior if no filename provided
      window.open(blobUrl, '_blank');
    }
  } catch (error) {
    console.error('Error opening PDF:', error);
    alert('Fehler beim Öffnen des PDFs');
  }
};

/**
 * Save the PDF to the user's device
 * @param {jsPDF} doc - The PDF document to save
 * @param {string} filename - The filename to use
 */
export const savePdf = (doc, filename) => {
  try {
    doc.save(filename || 'query-tool-results.pdf');
  } catch (error) {
    console.error('Error saving PDF:', error);
    alert('Fehler beim Speichern des PDFs');
  }
};